import React, { FC, useEffect, useState } from 'react';
import { LandscapeProjectsAddProjectsRequestedSupportTemplate } from '../../templates';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../../atoms/Spinner/Spinner';
import LandscapeProjectNotSavedInfoPage from '../LandscapeProjectNotSavedInfoPage';
import { ProjectSupportResponseDto } from '../../../store/landscapeAPI/project/dto/response/project-support-response.dto';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchProjectById } from '../../../store/landscapeAPI/project/reducers/project.slice';
import { ProjectSupportType } from '../../../store/landscapeAPI/project/project-support-type';
import { ProjectInKindSupport } from '../../../store/landscapeAPI/project/project-in-kind-support';
import {
  deleteProjectSupport,
  fetchProjectSupportsByProjectId,
  saveProjectSupport,
  updateProjectSupport,
} from '../../../store/landscapeAPI/project/reducers/project-support.slice';
import { ProjectSupportSaveRequestDto } from '../../../store/landscapeAPI/project/dto/request/project-support-save-request.dto';
import { LocalesLanguagesConstants } from '../../../locales/locales';

export interface ProjectSupportState {
  projectFinancing: ProjectSupportResponseDto;
  projectInKindSupport: ProjectSupportResponseDto[];
  projectContainsFinancialSupport: boolean;
  projectContainsInKindSupport: boolean;
  isTrainingChecked: boolean;
  isTechnologyChecked: boolean;
  isAlternativeChecked: boolean;
  isLandscapeChecked: boolean;
  isEnhancingChecked: boolean;
  isOtherChecked: boolean;
}

const LandscapeProjectsAddProjectsRequestedSupportPage: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { projectId } = useParams();

  const { enrichedProject, status } = useSelector(
    (state: RootState) => state.projectSlice
  );

  const [projectSupportState, setProjectSupportState] =
    useState<ProjectSupportState>({
      projectFinancing: {
        projectId: projectId,
        minimumFinancialContribution: 0,
        contributionGoal: 0,
        supportType: ProjectSupportType.Financial,
        inKindSupport: ProjectInKindSupport.NotRelevant,
        description: null,
        raised: 0,
      } as ProjectSupportResponseDto,
      projectInKindSupport: [] as ProjectSupportResponseDto[],
      projectContainsFinancialSupport: false,
      projectContainsInKindSupport: false,
      isTrainingChecked: false,
      isTechnologyChecked: false,
      isAlternativeChecked: false,
      isLandscapeChecked: false,
      isEnhancingChecked: false,
      isOtherChecked: false,
    });

  useEffect(() => {
    if (projectId && projectId !== 'new-project') {
      dispatch(
        fetchProjectById({
          id: projectId,
          language: LocalesLanguagesConstants.en,
        })
      );
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!enrichedProject) {
      return;
    }

    const financialSupport = enrichedProject.projectSupports.find(
      (support) => support.supportType === ProjectSupportType.Financial
    );

    const inKindSupport = enrichedProject.projectSupports.filter(
      (support) => support.supportType === ProjectSupportType.InKind
    );

    setProjectSupportState({
      projectFinancing: financialSupport || ({} as ProjectSupportResponseDto),
      projectInKindSupport: inKindSupport,
      projectContainsFinancialSupport: !!financialSupport,
      projectContainsInKindSupport: inKindSupport.length > 0,
      isTrainingChecked: inKindSupport.some(
        (support) =>
          support.inKindSupport === ProjectInKindSupport.TrainingAndExtension
      ),
      isTechnologyChecked: inKindSupport.some(
        (support) =>
          support.inKindSupport === ProjectInKindSupport.TechnologyTransfer
      ),
      isAlternativeChecked: inKindSupport.some(
        (support) =>
          support.inKindSupport ===
          ProjectInKindSupport.AlternativeLivelihoodsActivities
      ),
      isLandscapeChecked: inKindSupport.some(
        (support) =>
          support.inKindSupport === ProjectInKindSupport.LandscapeRestoration
      ),
      isEnhancingChecked: inKindSupport.some(
        (support) =>
          support.inKindSupport ===
          ProjectInKindSupport.EnhancingAccessToFinance
      ),
      isOtherChecked: inKindSupport.some(
        (support) => support.inKindSupport === ProjectInKindSupport.Other
      ),
    });
  }, [enrichedProject]);

  const { status: supportStatus } = useSelector(
    (state: RootState) => state.projectSupportSlice
  );

  const handleSubmit = async () => {
    await dispatch(fetchProjectSupportsByProjectId(projectId!))
      .unwrap()
      .then((supports) => updateProjectSupports(supports));
  };

  const updateProjectSupports = async (
    existingSupports: ProjectSupportResponseDto[]
  ) => {
    const supportsToPost: ProjectSupportResponseDto[] = [];

    const supportsToDelete: ProjectSupportResponseDto[] = [];

    let supportToUpdate: ProjectSupportResponseDto | null = null;

    projectSupportState.projectInKindSupport.forEach((localSupport) => {
      const existsInAPI = existingSupports.some(
        (apiSupport) => apiSupport.inKindSupport === localSupport.inKindSupport
      );
      if (!existsInAPI) {
        supportsToPost.push(localSupport);
      }
    });

    existingSupports.forEach((apiSupport) => {
      const existsInLocal = projectSupportState.projectInKindSupport.some(
        (localSupport) =>
          localSupport.inKindSupport === apiSupport.inKindSupport
      );
      if (!existsInLocal) {
        supportsToDelete.push(apiSupport);
      }
    });

    const existingFinancialSupport = existingSupports.find(
      (support) => support.supportType === ProjectSupportType.Financial
    );

    if (existingFinancialSupport) {
      supportToUpdate = {
        ...existingFinancialSupport,
        contributionGoal:
          projectSupportState.projectFinancing.contributionGoal ?? 0,
        minimumFinancialContribution:
          projectSupportState.projectFinancing.minimumFinancialContribution ??
          0,
        raised: projectSupportState.projectFinancing.raised ?? 0,
      };
    } else if (projectSupportState.projectContainsFinancialSupport) {
      supportsToPost.push({
        ...projectSupportState.projectFinancing,
        contributionGoal:
          projectSupportState.projectFinancing.contributionGoal ?? 0,
        minimumFinancialContribution:
          projectSupportState.projectFinancing.minimumFinancialContribution ??
          0,
        raised: projectSupportState.projectFinancing.raised ?? 0,
        supportType: ProjectSupportType.Financial,
        inKindSupport: ProjectInKindSupport.NotRelevant,
        projectId: projectId!,
        published: true,
      });
    }

    await Promise.all(
      supportsToPost.map((support) => {
        const supportRequest: ProjectSupportSaveRequestDto = {
          contributionGoal: support.contributionGoal,
          description: support.description,
          id: support.id,
          inKindSupport: support.inKindSupport,
          minimumFinancialContribution: support.minimumFinancialContribution,
          projectId: support.projectId,
          published: true,
          raised: support.raised,
          supportType: support.supportType,
        };
        return dispatch(saveProjectSupport(supportRequest)).unwrap();
      })
    );

    await Promise.all(
      supportsToDelete.map((support) => {
        return dispatch(deleteProjectSupport(support.id)).unwrap();
      })
    );

    if (supportToUpdate) {
      await dispatch(updateProjectSupport(supportToUpdate));
    }

    await dispatch(
      fetchProjectById({
        id: projectId!,
        language: LocalesLanguagesConstants.en,
      })
    ).unwrap();
  };

  if (status === 'loading' || supportStatus === 'loading') {
    return <Spinner />;
  }

  return enrichedProject ? (
    <LandscapeProjectsAddProjectsRequestedSupportTemplate
      projectSupportState={projectSupportState}
      setProjectSupportState={setProjectSupportState}
      handleSubmit={handleSubmit}
    />
  ) : (
    <LandscapeProjectNotSavedInfoPage />
  );
};

export default LandscapeProjectsAddProjectsRequestedSupportPage;
